/**
 * @file billingPeriod.ts
 * @brief The one "this period" every Billing screen uses, plus the small
 *        readers for usage and invoice rows.
 *
 * @details
 * "Month" meant four things across the apps (usage audit 16-10-2026). Here
 * there is one rule, always the server's window:
 * - on a plan: `usage.periodStart` → `usage.periodEnd` from
 *   GET /billing/subscriptions. That is the minutes window: a month slice on an
 *   annual plan, NOT `currentPeriodEnd` (the renewal date, up to a year away,
 *   bug B11);
 * - with no plan: the Ask allowance's window, which the server takes as the
 *   UTC calendar month (`resolveSearchPeriod(null)`), so the dates match what
 *   it counts Asks over.
 * Every screen labels it with its dates.
 *
 * Pure (relative imports only) so node:test can load it.
 *
 * @author DES
 * @date 17-10-2026
 */

export type BillingPeriod = {
  start: Date;
  end: Date;
  source: "plan" | "calendar_month";
};

function toDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The UTC calendar month containing `now` (the server's no-plan window). */
export function utcCalendarMonth(now: Date): { start: Date; end: Date } {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return { start, end };
}

export function resolveBillingPeriod(
  usage: { periodStart?: string | null; periodEnd?: string | null } | null | undefined,
  now: Date = new Date(),
  /** The server's no-plan window (`period` on GET /billing/subscriptions, 18-10-2026). */
  serverPeriod?: { start?: string | null; end?: string | null } | null,
): BillingPeriod {
  const start = toDate(usage?.periodStart);
  const end = toDate(usage?.periodEnd);
  if (start && end && end > start) return { start, end, source: "plan" };
  const s2 = toDate(serverPeriod?.start);
  const e2 = toDate(serverPeriod?.end);
  if (s2 && e2 && e2 > s2) return { start: s2, end: e2, source: "calendar_month" };
  return { ...utcCalendarMonth(now), source: "calendar_month" };
}

/**
 * "25 Sept – 24 Oct 2026". The end shown is the last day IN the window (the
 * server's end is exclusive). A plan window is read in the viewer's time zone:
 * Stripe stamps it at UK midnight (23:00Z in summer), and "Renews on" beside it
 * is local too, so a UTC label would be a day early. The no-plan window IS a
 * UTC month on the server, so it stays UTC.
 */
export function periodLabel(
  p: { start: Date; end: Date; source?: BillingPeriod["source"] },
  timeZone: string | undefined = p.source === "plan" ? undefined : "UTC",
): string {
  const day = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone });
  const dayYear = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone });
  const lastDay = new Date(p.end.getTime() - 1);
  const year = (d: Date) => new Intl.DateTimeFormat("en-GB", { year: "numeric", timeZone }).format(d);
  return `${(year(p.start) === year(lastDay) ? day : dayYear).format(p.start)} – ${dayYear.format(lastDay)}`;
}

/** The window as the invoices feed's `from` / `to` query (inclusive ISO). */
export function periodQuery(p: { start: Date; end: Date }): { from: string; to: string } {
  return { from: p.start.toISOString(), to: new Date(p.end.getTime() - 1).toISOString() };
}

export type MinutesBar = {
  used: number;
  included: number;
  left: number;
  /** 0..1 for the bar */
  fraction: number;
  over: number;
};

export function minutesBar(used: number | null | undefined, included: number | null | undefined): MinutesBar | null {
  if (included == null || !Number.isFinite(included) || included <= 0) return null;
  const u = Math.max(0, Math.floor(Number(used) || 0));
  return {
    used: u,
    included,
    left: Math.max(0, included - u),
    fraction: Math.min(1, u / included),
    over: Math.max(0, u - included),
  };
}

/** Ask line: "12 of 300 used", or "12 used · not limited yet". */
export function askLine(q: { used: number; limit: number; enforced: boolean } | null | undefined): string | null {
  if (!q) return null;
  if (!q.enforced) return `${q.used} used · not limited yet`;
  return `${q.used} of ${q.limit} used · ${Math.max(0, q.limit - q.used)} left`;
}

// ----------------------------------------------------------------------
// Invoice rows (GET /invoices, unified feed)
// ----------------------------------------------------------------------

export type InvoiceRowLike = {
  product?: string | null;
  amount: number;
  status?: string | null;
  /** Captured amount when the server sends it (not yet; see report). */
  chargedCents?: number | null;
  capturedCents?: number | null;
  chargeCents?: number | null;
  minutes?: number | null;
  durationSeconds?: number | null;
  paymentFailed?: boolean | null;
  refunded?: boolean | null;
  refundedCents?: number | null;
};

const PRODUCT_LABELS: Record<string, string> = {
  conversation: "Interpretation",
  boardroom: "Meeting Intelligence",
  note: "Voice note",
  subscription: "Plan",
};

export function invoiceItemLabel(product: string | null | undefined): string {
  return PRODUCT_LABELS[String(product ?? "")] ?? "Usage";
}

export function invoiceMinutes(row: InvoiceRowLike): number | null {
  if (row.minutes != null && Number.isFinite(row.minutes)) return row.minutes;
  if (row.durationSeconds != null && Number.isFinite(row.durationSeconds)) {
    return Math.max(1, Math.ceil(row.durationSeconds / 60));
  }
  return null;
}

/**
 * Pay-as-you-go spend in a period from the feed's aggregates: everything
 * billed minus plan charges. Only exact with one currency; with several the
 * caller shows each currency total as it is.
 */
export function paygSpend(data: {
  totalsByCurrency?: { currency: string; amountCents: number }[];
  subscriptionAmountCents?: number;
} | null | undefined): { currency: string; cents: number } | null {
  const totals = data?.totalsByCurrency ?? [];
  if (totals.length === 0) return null;
  if (totals.length > 1) return null;
  const t = totals[0]!;
  return { currency: t.currency, cents: Math.max(0, t.amountCents - (data?.subscriptionAmountCents ?? 0)) };
}

// ----------------------------------------------------------------------
// Pace through the period (Usage tab, DES 21-10-2026)
// ----------------------------------------------------------------------

const DAY_MS = 86_400_000;

export type UsagePace = {
  /** Whole days in the window, and the day today is in (1-based). */
  totalDays: number;
  day: number;
  daysLeft: number;
  /** How far through the window time is, and the allowance is (0..1). */
  timeFraction: number;
  usedFraction: number;
  /** Minutes the period ends on at today's rate (null while too early to tell). */
  projectedUsed: number | null;
  /** When the allowance runs out at today's rate, if before the period ends. */
  runOutAt: Date | null;
  /**
   * idle: nothing used yet · early: under 2 days in, too soon to project ·
   * on_track: the rate fits the allowance · ahead: it runs out before the end
   * · over: already past the allowance.
   */
  status: "idle" | "early" | "on_track" | "ahead" | "over";
};

/** Where usage stands against the calendar: no server field, just used, included and the window. */
export function usagePace(used: number, included: number, period: { start: Date; end: Date }, now: Date = new Date()): UsagePace | null {
  const span = period.end.getTime() - period.start.getTime();
  if (!(span > 0) || !(included > 0)) return null;
  const elapsedMs = Math.min(Math.max(now.getTime() - period.start.getTime(), 0), span);
  const totalDays = Math.max(1, Math.round(span / DAY_MS));
  const day = Math.min(totalDays, Math.floor(elapsedMs / DAY_MS) + 1);
  const daysLeft = Math.max(0, Math.ceil((span - elapsedMs) / DAY_MS));
  const timeFraction = elapsedMs / span;
  const u = Math.max(0, used);
  const usedFraction = Math.min(1, u / included);
  const base = { totalDays, day, daysLeft, timeFraction, usedFraction };
  if (u >= included) return { ...base, projectedUsed: null, runOutAt: null, status: "over" };
  if (u === 0) return { ...base, projectedUsed: null, runOutAt: null, status: "idle" };
  if (elapsedMs < 2 * DAY_MS) return { ...base, projectedUsed: null, runOutAt: null, status: "early" };
  const perMs = u / elapsedMs;
  const projectedUsed = Math.round(perMs * span);
  if (projectedUsed <= included) return { ...base, projectedUsed, runOutAt: null, status: "on_track" };
  const runOutAt = new Date(period.start.getTime() + included / perMs);
  return { ...base, projectedUsed, runOutAt, status: "ahead" };
}
